import { z } from "zod";
import { dbIntIdSchema } from "@common/schemas/shared.schema";
import {
    OrganizationMemberClientAccessSort,
    OrganizationMemberClientAccessSource,
} from "@common/types/organizationMemberClientAccess";

export const recordOrganizationMemberClientAccessBodySchema = z.object({
    clientWorkspaceId: z.string().uuid(),
    previousWorkspaceId: z.string().uuid().nullable().optional(),
    source: z.nativeEnum(OrganizationMemberClientAccessSource),
});

export type RecordOrganizationMemberClientAccessBody = z.infer<
    typeof recordOrganizationMemberClientAccessBodySchema
>;

// Upper bound on a single list request's `limit`. Mirrors
// INVOICE_TEMPLATE_LIST_MAX_LIMIT / listCatalogItemsQuerySchema so a caller
// can never ask for an unbounded page of audit rows.
export const ORGANIZATION_MEMBER_CLIENT_ACCESS_LIST_MAX_LIMIT = 200;

export const listOrganizationMemberClientAccessQuerySchema = z.object({
    // Both default so an omitted query still returns a bounded, ordered page
    // (no unpaginated "full matching set" mode for this endpoint — this is an
    // audit log, not a lookup list).
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(ORGANIZATION_MEMBER_CLIENT_ACCESS_LIST_MAX_LIMIT)
        .default(20),
    // Deliberately NOT named `workspaceId`: any query/body/path param with
    // that exact key trips ResourceAccessPairingInterceptor's fail-closed
    // check, which demands a WorkspaceAccessGuard on the route. This route is
    // scoped by OrganizationAccessGuard + an explicit organization-membership
    // check instead (full workspace membership is not required to read this
    // audit log), so the field is named after the entity column it filters
    // on (matches `clientWorkspaceId` on the POST body / entity).
    clientWorkspaceId: z.string().uuid().optional(),
    memberId: dbIntIdSchema.optional(),
    sort: z
        .nativeEnum(OrganizationMemberClientAccessSort)
        .default(OrganizationMemberClientAccessSort.CREATED_AT_DESC),
});

export type ListOrganizationMemberClientAccessQuery = z.infer<
    typeof listOrganizationMemberClientAccessQuerySchema
>;

export const organizationMemberClientAccessItemSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    userId: z.number().int(),
    clientWorkspaceId: z.string().uuid(),
    previousWorkspaceId: z.string().uuid().nullable(),
    source: z.nativeEnum(OrganizationMemberClientAccessSource),
    createdAt: z.coerce.date(),
});

export type OrganizationMemberClientAccessItem = z.infer<
    typeof organizationMemberClientAccessItemSchema
>;

export const listOrganizationMemberClientAccessResponseSchema = z.object({
    data: z.array(organizationMemberClientAccessItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageCount: z.number().int(),
});

export type ListOrganizationMemberClientAccessResponse = z.infer<
    typeof listOrganizationMemberClientAccessResponseSchema
>;
